import { useCallback, useState } from 'react';
import { View } from 'react-big-calendar';
import { MedicationEvent } from '../types';

export const useScheduleHandlers = () => {
  const [selectedEvent, setSelectedEvent] = useState<MedicationEvent | null>(
    null,
  );
  const [popupAnchor, setPopupAnchor] = useState<HTMLElement | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>('week');
  const [overflowEvents, setOverflowEvents] = useState<MedicationEvent[]>([]);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(
    null,
  );

  const handleSelectEvent = useCallback(
    (event: MedicationEvent, e: React.SyntheticEvent) => {
      setSelectedEvent(event);
      setPopupAnchor(e.currentTarget as HTMLElement);
      setOverflowAnchor(null);
    },
    [],
  );

  // Handle click on a slot's "+N" block
  const handleSelectOverflow = useCallback(
    (events: MedicationEvent[], e: React.SyntheticEvent) => {
      setOverflowEvents(events);
      setOverflowAnchor(e.currentTarget as HTMLElement);
    },
    [],
  );

  // Picking an event out of the overflow list anchors the detail popup to the
  // "+N" block, not the clicked row: closing this popover unmounts that row,
  // and MUI repositions from the anchor on every later render.
  const handleSelectOverflowEvent = useCallback(
    (event: MedicationEvent) => {
      setSelectedEvent(event);
      setPopupAnchor(overflowAnchor);
      setOverflowAnchor(null);
    },
    [overflowAnchor],
  );

  const handleOverflowClose = useCallback(() => {
    setOverflowAnchor(null);
    setOverflowEvents([]);
  }, []);

  // Handle popup close
  const handlePopupClose = useCallback(() => {
    // Note: Keeping selectedEvent to prevent modal from losing data
    setPopupAnchor(null);
  }, []);

  // Handle edit action
  const handleEdit = useCallback(() => {
    setEditModalOpen(true);
    handlePopupClose();
  }, [handlePopupClose]);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditModalOpen(false);
    // Clear selected event when modal is closed
    setSelectedEvent(null);
  }, []);

  return {
    // State
    selectedEvent,
    popupAnchor,
    editModalOpen,
    currentDate,
    currentView,
    overflowEvents,
    overflowAnchor,
    // Handlers
    handleSelectEvent,
    handleSelectOverflow,
    handleSelectOverflowEvent,
    handleOverflowClose,
    handlePopupClose,
    handleEdit,
    handleEditModalClose,
    // Setters for calendar navigation
    setCurrentDate,
    setCurrentView,
  };
};
